<template>
  <base-modal
    show
    size="md"
    :has-close-btn="!globalModals.length"
    :modal-id="MODAL_ID"
    :heading="heading"
    :cancel-button="false"
    @close:modal="closeModal"
  >
    <template v-if="formStep === STEP_CART" #fullWidth>
      <token-purchase-pack
        cart
        :heading="$t('item')"
        :selected-pack="selectedPack"
        :token-name="tokenName"
        :game-name="game.name"
        :is-loading="cartLoading"
      />
      <div :id="TILIA_WIDGET" class="tw-px-6" />
    </template>
    <template v-if="formStep === STEP_CHECKOUT">
      <token-purchase-pack
        :heading="$t('marketplace.order_summary')"
        :selected-pack="selectedPack"
        :token-name="tokenName"
        :game-name="game.name"
        :tax="dataCheckout.tax || 0"
      />

      <template v-if="paymentMethods">
        <base-divider class="tw-my-6" />
        <p
          class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-mb-3"
        >
          {{ $t('marketplace.payment_method', paymentMethods.length) }}
        </p>

        <div class="tw-space-y-4">
          <div
            v-for="method in paymentMethods"
            :key="method.payment_method_id"
            class="tw-flex tw-items-end"
          >
            <base-icon :icon="method.provider" class="tw-h-5 tw-fill-current" />
            <p class="tw-text-right tw-ml-auto">
              {{ method.display }}
            </p>
            <p v-if="paymentMethods.length > 1" class="tw-text-right tw-w-14">
              {{ humanPriceFormat(convertToDecimal(getAmount(method.amount))) }}
            </p>
          </div>
        </div>
      </template>

      <base-divider class="tw-my-6" />

      <p
        class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-mb-3"
      >
        {{ $t('marketplace.wallet_balance') }}
      </p>
      <div class="tw-flex tw-w-full tw-justify-between">
        <div class="tw-flex tw-items-center tw-gap-x-2 tw-opacity-70">
          <font-awesome-icon icon="wallet" size="lg" />
          <p>{{ $t('marketplace.vc_balance') }}</p>
        </div>
        <span
          >{{
            deficit > balance
              ? (balance - deficit).toLocaleString()
              : balance.toLocaleString()
          }}
          {{ tokenName }}</span
        >
      </div>
      <div class="tw-flex tw-w-full tw-justify-between">
        <span class="tw-opacity-70">{{ $t('marketplace.balance_after') }}</span>
        <span class="tw-font-bold"
          >{{
            (deficit > balance
              ? (balance - deficit + selectedPack.amount).toLocaleString()
              : balance + selectedPack.amount
            ).toLocaleString()
          }}
          {{ tokenName }}
        </span>
      </div>
    </template>
    <template v-if="formStep === STEP_RECEIPT">
      <p class="tw-opacity-70 tw-mb-5">
        {{ $t('marketplace.token_purchase_success', { game: game.name }) }}
      </p>

      <p
        class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-mb-3"
      >
        {{ $t('summary') }}
      </p>
      <table class="tw-mb-5">
        <tbody>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">{{ $t('date') }}</td>
            <td>{{ dataPurchase.purchase_date }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.transaction_id') }}
            </td>
            <td>{{ dataPurchase.transaction_id }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">{{ $t('item') }}</td>
            <td>
              {{ selectedPack.amount.toLocaleString() }}
              {{ tokenName }}
            </td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.payment_method') }}
            </td>
            <td>{{ paymentMethods.map((m) => m.display).join(', ') }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.total_paid') }}
            </td>
            <td>
              {{
                humanPriceFormat(convertToDecimal(dataPurchase.gross_amount))
              }}
            </td>
          </tr>
        </tbody>
      </table>

      <div
        class="tw-border-2 tw-border-primary mod-global--border-radius tw-p-3 tw-flex tw-justify-between"
      >
        <div class="tw-flex tw-items-center tw-gap-x-2">
          <font-awesome-icon icon="wallet" size="lg" />
          <p>{{ $t('marketplace.wallet_balance') }}</p>
        </div>
        <loading-title v-if="loadingCreateWallet" />
        <p v-else class="tw-text-h6">
          <span class="tw-font-bold"
            >{{
              (deficit > balance ? balance - deficit : balance).toLocaleString()
            }}
            {{ tokenName }}
          </span>
        </p>
      </div>
    </template>

    <template v-if="formStep !== STEP_CART" #footer-top>
      <base-checkbox
        v-if="formStep !== STEP_RECEIPT"
        class="tw--mt-3 tw-mb-3"
        large
        :error-gap="false"
        :background="false"
        :errors="errors.monTermsAgreed"
        :model-value="monTermsAgreed"
        @input="(input) => updateAgreed(input)"
      >
        <template #label>
          <span class="tw-text-theme">
            <i18n-t keypath="marketplace.agreement">
              <template #terms>
                <a
                  class="mod-link"
                  href="https://mod.io/terms"
                  @click.prevent="openTermsOfUseModal()"
                  >{{ $t('terms') }}
                </a>
              </template>
              <template #refund>
                <a
                  class="mod-link"
                  href="https://mod.io/refund"
                  @click.prevent="openRefundPolicyModal()"
                  >{{ $t('refund') }}</a
                >
              </template>
            </i18n-t>
          </span>
        </template>
      </base-checkbox>
    </template>

    <template v-if="formStep !== STEP_CART" #footer>
      <base-button
        v-if="formStep === STEP_CHECKOUT"
        secondary
        :disabled="loadingPurchase"
        class="tw-w-full sm:tw-w-auto tw-min-w-36"
        @click="returnToPaymentMethods"
      >
        {{ $t('marketplace.change_payment') }}
      </base-button>
      <base-button
        :primary="formStep === STEP_CHECKOUT"
        :secondary="formStep === STEP_RECEIPT"
        :disabled="loadingPurchase"
        :errors="errors"
        class="tw-w-full sm:tw-w-auto tw-min-w-36"
        :status="formStep !== STEP_RECEIPT ? status : null"
        @click="button.onClick"
      >
        {{ button.text }}
      </base-button>
      <base-button
        v-if="formStep === STEP_RECEIPT && mod"
        primary
        class="tw-w-full sm:tw-w-auto tw-min-w-36"
        @click="backToModPurchase"
      >
        {{
          $t('marketplace.back_to_mod', {
            ugc: ugcName(game, true, true).value,
          })
        }}
      </base-button>
    </template>
  </base-modal>

  <teleport to="body">
    <cancel-transaction-modal
      v-if="isCancelModalOpen"
      @confirm="closeModal(true)"
      @close:modal="hideCancelModal()"
    />
  </teleport>
</template>

<script>
import TokenPurchasePack from '@components/Monetization/Components/TokenPurchasePack.vue'
import CancelTransactionModal from '@shared/components/Modals/CancelTransactionModal.vue'
import { AGREEMENT_TYPE_REFUND, AGREEMENT_TYPE_TOU } from '@config/options.js'
import { setGlobalModal, globalModals } from '@composables/useModal.js'
import { onMounted, computed, nextTick, toRefs, ref } from 'vue'
import LoadingTitle from '@components/Loading/LoadingTitle.vue'
import { initTiliaWidget } from '@shared/js/plugins/tilia.js'
import { MODAL_AGREEMENT } from '@config/globalModals.js'
import { humanDateTimeFormat } from '@helpers/utils.js'
import { monetizationStore, authStore } from '@stores'
import { monTermsConfig } from '@config/validation.js'
import { TILIA_WIDGET } from '@config/htmlIDs.js'
import { useI18n } from 'vue-i18n'
import {
  getOrCreateBuyerGameWalletRequest,
  getAccountPaymentMethodsRequest,
  checkoutCompleteRequest,
  checkoutRequest,
} from '@services'
import {
  useResourceValue,
  useMonetization,
  useValidate,
  useStatus,
  useRoute,
  useModal,
  useAsync,
} from '@composables'
import {
  TILIA_TYPE_PAYMENT,
  TILIA_STATE_CANCEL,
  TILIA_STATE_ERROR,
} from '@config/monetization.js'
import {
  GAME_FEATURED_ROUTE,
  MOD_BROWSE_ROUTE,
  MOD_VIEW_ROUTE,
} from '@config/routeNames.js'
import {
  humanPriceFormat,
  convertToDecimal,
} from '@helpers/monetizationUtils.js'

export const STEP_CART = 'cart'
export const STEP_CHECKOUT = 'checkout'
export const STEP_RECEIPT = 'receipt'

const MODAL_ID = 'purchase-modal'

export default {
  components: {
    CancelTransactionModal,
    TokenPurchasePack,
    LoadingTitle,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    mod: {
      type: Object,
      default: null,
    },
    selectedPack: {
      type: Object,
      required: true,
    },
    defaultStep: {
      type: String,
      default: STEP_CART,
    },
  },
  emits: ['close:purchasemodal'],
  setup(props, { emit }) {
    const { game, selectedPack, defaultStep, mod } = toRefs(props)

    const { tokenName: getTokenName, ugcName } = useResourceValue()
    const { push, previousRoute } = useRoute()
    const {
      isModalOpen: isCancelModalOpen,
      showModal: showCancelModal,
      hideModal: hideCancelModal,
    } = useModal()

    const balance = ref(0)
    const deficit = ref(0)
    const paymentMethods = ref(null)
    const formStep = ref(defaultStep.value)
    const monTermsAgreed = ref(false)
    const tokenName = game.value.token_name || getTokenName().value

    const { isMonRegistered } = authStore()
    const { transactionKey, setTransactionKey } = monetizationStore()
    const { validate, errors, hasErrors } = useValidate(
      monTermsConfig.validation
    )
    const { t } = useI18n()
    const {
      loadingRegister,
      loadingSession,
      setGameWallet,
      registerUser,
      purchaseMod,
      getSession,
      unsetNonce,
      nonce,
    } = useMonetization()

    const {
      loading: loadingCreateWallet,
      error: errorCreateWallet,
      data: dataCreateWallet,
      run: runCreateWallet,
    } = useAsync(
      () => getOrCreateBuyerGameWalletRequest(game.value.id),
      t('marketplace.error_wallet')
    )

    const {
      error: paymentMethodsError,
      data: paymentMethodsData,
      run: runPaymentMethods,
    } = useAsync(getAccountPaymentMethodsRequest)

    // Checkout process
    const {
      error: errorCheckout,
      data: dataCheckout,
      run: runCheckout,
    } = useAsync(checkoutRequest, t('marketplace.error_process'))

    // Purchase
    const {
      loading: loadingPurchase,
      error: errorPurchase,
      data: dataPurchase,
      run: runPurchase,
    } = useAsync(checkoutCompleteRequest, t('marketplace.error_complete'))

    const cartLoading = computed(
      () => loadingRegister.value || loadingSession.value
    )

    const packTransactionKey = computed(
      () =>
        transactionKey.value?.[
          `${game.value.id}-${selectedPack.value.token_pack_id}`
        ]
    )

    const { updateStatus, statusType, status } = useStatus({
      loading: loadingPurchase,
      error: errorPurchase,
    })

    const heading = computed(() => {
      switch (formStep.value) {
        case STEP_CART:
          return t('marketplace.order_summary')

        case STEP_CHECKOUT:
          return t('marketplace.checkout')

        case STEP_RECEIPT:
          return t('marketplace.vc_topup_confirmed')
      }

      return ''
    })

    const button = computed(() => {
      switch (formStep.value) {
        case STEP_CHECKOUT:
          return {
            text: t('marketplace.confirm_purchase'),
            onClick: purchase,
          }

        case STEP_RECEIPT:
          return {
            text: t('marketplace.continue_browsing'),
            onClick: closeModal,
          }
      }

      return {}
    })

    onMounted(async () => {
      if (!packTransactionKey.value) {
        setTransactionKey(
          game.value.id,
          selectedPack.value.token_pack_id,
          Date.now().toString(36)
        )
      }

      if (!isMonRegistered.value) {
        await registerUser()
      }

      getWalletBalance()
      const sessionHasError = await getSession()
      if (sessionHasError) closeModal(true)

      initTiliaWidget({
        flow: TILIA_TYPE_PAYMENT,
        redirect: nonce.value,
        callback: onComplete,
        transactionAmount: selectedPack.value.price,
      })
    })

    async function getWalletBalance() {
      await runCreateWallet()

      if (!errorCreateWallet.value) {
        balance.value = dataCreateWallet.value.balance
        deficit.value = dataCreateWallet.value.deficit
        setGameWallet(game.value.id, dataCreateWallet.value)
      }
    }

    function onComplete(result) {
      if (
        result.state === TILIA_STATE_CANCEL ||
        result.state === TILIA_STATE_ERROR
      ) {
        closeModal(true)
      } else {
        result.paymentMethods.sort((a, b) => b.amount - a.amount)
        checkout(result)
      }
    }

    function closeModal(confirm = false) {
      if (confirm || formStep.value !== STEP_CHECKOUT) {
        emit('close:purchasemodal')
        setTransactionKey(game.value.id, selectedPack.value.token_pack_id, null)
        unsetNonce()
        return
      }

      showCancelModal()
    }

    function updateAgreed(input) {
      monTermsAgreed.value = input
      validate('monTermsAgreed', monTermsAgreed.value)
    }

    function openRefundPolicyModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: {
          agreementType: AGREEMENT_TYPE_REFUND,
          noOpacity: true,
        },
      })
    }

    function openTermsOfUseModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_TOU, noOpacity: true },
      })
    }

    function updateStep(step) {
      formStep.value = step

      switch (step) {
        case STEP_CART:
          nextTick(() => {
            initTiliaWidget({
              flow: TILIA_TYPE_PAYMENT,
              redirect: nonce.value,
              callback: onComplete,
              transactionAmount: selectedPack.value.price,
            })
          })
          break
      }
    }

    function returnToPaymentMethods() {
      setTransactionKey(
        game.value.id,
        selectedPack.value.token_pack_id,
        Date.now().toString(36)
      )
      updateStep(STEP_CART)
    }

    function getAmount(amount) {
      if (amount > 0) return amount

      const otherAmount = paymentMethods.value.reduce(
        (max, method) => Math.max(max, method.amount),
        0
      )
      return Math.max(selectedPack.value.price - otherAmount, 0)
    }

    async function checkout(result) {
      await runPaymentMethods()

      if (!paymentMethodsError.value) {
        paymentMethods.value = result.paymentMethods.map((m) => {
          const matchingMethod =
            paymentMethodsData.value.data?.[m.payment_method_id]
          if (matchingMethod) {
            m.display = matchingMethod.display_string
            m.provider = matchingMethod.method_class
          }
          return m
        })

        // create a pending transaction
        process()
      }
    }

    async function process() {
      const data = {
        payment_method_ids: paymentMethods.value,
        token_pack_id: selectedPack.value.token_pack_id,
        game_id: game.value.id,
        display_amount: selectedPack.value.price,
        idempotent_key: packTransactionKey.value,
      }

      await runCheckout(data)

      if (!errorCheckout.value) {
        updateStep(STEP_CHECKOUT)
      } else {
        updateStep(STEP_CART)
        paymentMethods.value = null
      }
    }

    async function purchase() {
      validate('monTermsAgreed', monTermsAgreed.value)
      if (hasErrors()) {
        updateStatus(statusType.ERROR)
        return
      }

      const data = {
        transaction_id: dataCheckout.value.transaction_id,
        payment_method_ids: paymentMethods.value,
        display_amount: selectedPack.value.price,
        token_pack_id: selectedPack.value.token_pack_id,
        game_id: game.value.id,
        terms_accepted: true,
        refund_accepted: true,
        idempotent_key: packTransactionKey.value,
      }

      // attempt to complete purchase
      await runPurchase(data)

      if (!errorPurchase.value) {
        updateStep(STEP_RECEIPT)
        updateStatus(statusType.SUCCESS)
        dataPurchase.value.purchase_date = humanDateTimeFormat(
          dataPurchase.value.purchase_date
        )
        getWalletBalance()

        setTransactionKey(game.value.id, selectedPack.value.token_pack_id, null)
      }
    }

    function backToModPurchase() {
      purchaseMod.value = mod.value
      const modView = previousRoute.value.name === MOD_VIEW_ROUTE
      const featuredRoute = previousRoute.value.name === GAME_FEATURED_ROUTE
      push({
        name: modView
          ? MOD_VIEW_ROUTE
          : featuredRoute
            ? GAME_FEATURED_ROUTE
            : MOD_BROWSE_ROUTE,
        params: modView ? { mod: purchaseMod.value.name_id } : null,
      })
    }

    return {
      returnToPaymentMethods,
      openRefundPolicyModal,
      openTermsOfUseModal,
      loadingCreateWallet,
      backToModPurchase,
      isCancelModalOpen,
      humanPriceFormat,
      convertToDecimal,
      loadingPurchase,
      hideCancelModal,
      paymentMethods,
      monTermsAgreed,
      STEP_CHECKOUT,
      TILIA_WIDGET,
      STEP_RECEIPT,
      updateAgreed,
      dataCheckout,
      dataPurchase,
      globalModals,
      cartLoading,
      purchaseMod,
      closeModal,
      tokenName,
      getAmount,
      STEP_CART,
      MODAL_ID,
      formStep,
      ugcName,
      balance,
      deficit,
      heading,
      errors,
      button,
      status,
    }
  },
}
</script>
